import { useState, useEffect } from "react";
import TurndownService from "turndown";
import {
  X as CloseX, ClipboardText, MagicWand, Sparkle,
} from "@phosphor-icons/react";

/**
 * PasteFromWebModal — a one-click helper for editors who copy an article
 * from Wikipedia, a research site, or an FAO brief and want a cleaned-up
 * markdown block to paste into the crop article body.
 *
 * The pasted payload can be either:
 *   - Rich text (HTML from the clipboard, e.g. via Ctrl-C from a browser)
 *   - Plain text (fallback)
 *
 * We strip navigation, scripts, ads, sidebars, image frames and
 * "further reading" widgets, keep headings / paragraphs / lists /
 * blockquotes / links, and hand back GFM markdown ready to append to the
 * main body textarea.
 */

// Selectors we always drop before conversion — targets common WP / news
// site clutter without any external dependency.
const DROP_SELECTORS = [
  "script", "style", "noscript", "iframe", "form", "svg",
  "nav", "header", "footer", "aside",
  ".navbox", ".sidebar", ".infobox", ".mw-editsection", ".reference",
  ".hatnote", ".vertical-navbox", ".toc", ".mw-empty-elt",
  ".metadata", ".mbox-small", ".ambox", "sup.reference",
  "[role=navigation]", "[role=complementary]", "[role=banner]",
  "[aria-hidden=true]",
  ".advertisement", ".ad", ".ads",
  ".related-articles", ".further-reading",
  "figure", "img", "picture",   // articles are text-first — images will be re-added by the editor if needed
];

function cleanHtml(html) {
  // Wrap in a container so parsing works even without a root element.
  const dom = new DOMParser().parseFromString(
    `<div id="__paste_root__">${html}</div>`,
    "text/html",
  );
  const root = dom.getElementById("__paste_root__");
  if (!root) return "";

  // Strip clutter first.
  for (const sel of DROP_SELECTORS) {
    root.querySelectorAll(sel).forEach((el) => el.remove());
  }

  // Wikipedia-specific: drop bracketed citation refs like "[1]", "[edit]".
  root.querySelectorAll("*").forEach((el) => {
    if (el.childNodes.length === 1 && el.firstChild.nodeType === Node.TEXT_NODE) {
      const t = el.textContent.trim();
      if (/^\[\s*(edit|citation needed|\d+)\s*\]$/i.test(t)) el.remove();
    }
  });

  return root.innerHTML;
}

function toMarkdown(html) {
  const td = new TurndownService({
    headingStyle: "atx",       // # ## ###
    bulletListMarker: "-",
    codeBlockStyle: "fenced",
    emDelimiter: "_",
  });

  // Preserve blockquotes with inline emphasis intact.
  td.addRule("blockquote-cleanup", {
    filter: "blockquote",
    replacement: (content) =>
      content.split("\n").map((l) => (l ? `> ${l}` : ">")).join("\n").trim() + "\n",
  });

  // Skip any remaining <img> / <figure> we might have missed.
  td.remove(["img", "figure", "picture", "source"]);

  const raw = td.turndown(cleanHtml(html));

  // Post-process: collapse >2 blank lines, drop escaped bracket refs like
  // "\[1\]" that Wikipedia leaves behind, trim trailing whitespace per line.
  return raw
    .replace(/\\?\[\s*\d+\s*\\?\]/g, "")          // [1] and its escaped twin
    .replace(/\\?\[\s*(edit|citation needed)\s*\\?\]/gi, "")
    // Turndown emits "-   item" and "1.  item" — normalise to the common
    // "-  item" / "1. item" for cleaner-looking markdown in the editor.
    .replace(/^(\s*)- {2,}/gm, "$1- ")
    .replace(/^(\s*)(\d+)\. {2,}/gm, "$1$2. ")
    .split("\n")
    .map((l) => l.replace(/[ \t]+$/g, ""))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export default function PasteFromWebModal({ open, onClose, onApply }) {
  const [source, setSource] = useState("");     // raw pasted content (HTML or plain)
  const [markdown, setMarkdown] = useState(""); // cleaned markdown preview
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) { setSource(""); setMarkdown(""); setError(""); }
  }, [open]);

  useEffect(() => {
    if (!source.trim()) { setMarkdown(""); return; }
    try {
      const looksLikeHtml = /<[a-z][\s\S]*>/i.test(source);
      const md = looksLikeHtml
        ? toMarkdown(source)
        : source.trim();
      setMarkdown(md);
      setError("");
    } catch (e) {
      setError(String(e?.message || e));
      setMarkdown("");
    }
  }, [source]);

  // Try to grab rich HTML from the clipboard so users get proper formatting.
  const readFromClipboard = async () => {
    setError("");
    try {
      if (navigator.clipboard?.read) {
        const items = await navigator.clipboard.read();
        for (const it of items) {
          if (it.types.includes("text/html")) {
            const blob = await it.getType("text/html");
            const html = await blob.text();
            setSource(html);
            return;
          }
        }
      }
      const txt = await navigator.clipboard.readText();
      setSource(txt || "");
    } catch (e) {
      setError("Couldn't read the clipboard — paste manually into the box below.");
    }
  };

  const handlePaste = (e) => {
    // Prefer HTML from the clipboard event when the browser offers it.
    const html = e.clipboardData?.getData("text/html");
    if (html) {
      e.preventDefault();
      setSource(html);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[85] bg-[#1B4332]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      data-testid="paste-from-web-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl bg-[#F5EBDC] p-5 sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <MagicWand size={11} weight="fill" className="inline mr-1" />
              Paste from web
            </div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight">
              Clean a web article into markdown.
            </h2>
            <p className="mt-1 text-sm text-[#3A3830] max-w-lg">
              Copy an article from Wikipedia, the FAO or any research page,
              then paste (or click <em>Read clipboard</em>). Ads, sidebars,
              navigation and images are stripped — you keep the text,
              headings and lists.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="paste-from-web-close"
            className="shrink-0 rounded-full bg-white border border-black/10 p-1.5 hover:bg-black/5"
            aria-label="Close"
          >
            <CloseX size={14} weight="bold" />
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={readFromClipboard}
            data-testid="paste-from-web-clip"
            className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
          >
            <ClipboardText size={11} weight="fill" className="inline mr-1" />
            Read clipboard
          </button>
          <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]">
            or paste (Ctrl-V) into the box below
          </span>
        </div>

        <label className="mt-4 block text-xs font-mono-tech tracking-[0.28em] uppercase text-[#3A3830]">
          Source
        </label>
        <textarea
          value={source}
          onPaste={handlePaste}
          onChange={(e) => setSource(e.target.value)}
          data-testid="paste-from-web-source"
          placeholder="Paste HTML or plain text here…"
          className="mt-1 w-full h-40 rounded-lg border border-black/15 bg-white p-3 text-sm outline-none focus:border-black/40 resize-y"
        />

        {error && (
          <div className="mt-2 rounded-lg bg-red-100 text-red-900 text-xs p-2">
            {error}
          </div>
        )}

        <label className="mt-4 block text-xs font-mono-tech tracking-[0.28em] uppercase text-[#3A3830]">
          Cleaned markdown preview
        </label>
        <textarea
          value={markdown}
          readOnly
          data-testid="paste-from-web-preview"
          placeholder="The cleaned output will appear here — headings, lists and links kept intact."
          className="mt-1 w-full h-56 rounded-lg border border-black/15 bg-[#FBF2E1]/40 p-3 text-[14px] leading-relaxed font-serif-display outline-none resize-y"
        />
        <div className="mt-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3A3830]">
          {markdown ? `${markdown.split(/\s+/).length} words · ${markdown.length} chars` : "waiting for input…"}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-black/15 bg-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!markdown}
            data-testid="paste-from-web-apply-append"
            onClick={() => { onApply(markdown, "append"); onClose(); }}
            className="rounded-full border border-black/15 bg-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5 disabled:opacity-50"
          >
            Append to body
          </button>
          <button
            type="button"
            disabled={!markdown}
            data-testid="paste-from-web-apply-replace"
            onClick={() => { onApply(markdown, "replace"); onClose(); }}
            className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
          >
            <Sparkle size={11} weight="fill" className="inline mr-1" /> Replace body
          </button>
        </div>
      </div>
    </div>
  );
}
